import React, { useState, useEffect } from 'react';
import { 
  Zap, Rocket, Trash2, Terminal, AlertCircle, Sun, Moon, Monitor, 
  Save, LogOut, User, Database, Key, Cloud
} from 'lucide-react';
import { cn } from './lib/utils';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { TooltipProvider } from './components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import LoginForm from './components/LoginForm';
import InstallationForm from './components/InstallationForm';
import UninstallForm from './components/UninstallForm';
import ConsoleViewer from './components/ConsoleViewer';
import JobStatusPanel from './components/JobStatusPanel';
import ClusterManager from './components/ClusterManager';
import ClusterDashboard from './components/ClusterDashboard';
import AWSInfrastructure from './components/AWSInfrastructure';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import apiService from './services/api';
import './index.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [activeSection, setActiveSection] = useState('management'); // Default to Clusters
  const [activeTab, setActiveTab] = useState('install');
  const [backendStatus, setBackendStatus] = useState('checking');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [savedConfig, setSavedConfig] = useState(() => {
    const saved = localStorage.getItem('kafkaConfig');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterCredentials, setClusterCredentials] = useState(null);

  useEffect(() => {
    const session = localStorage.getItem('kafkaSession');
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        if (sessionData.expiry > Date.now()) {
          setIsAuthenticated(true);
          setCurrentUser(sessionData.username);
        } else {
          localStorage.removeItem('kafkaSession');
        }
      } catch (e) {
        localStorage.removeItem('kafkaSession');
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      checkBackendHealth();
      const interval = setInterval(checkBackendHealth, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    if (theme === 'system') {
      root.classList.add(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    } else {
      root.classList.add(theme);
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const checkBackendHealth = async () => {
    try {
      await apiService.healthCheck();
      setBackendStatus('healthy');
    } catch {
      setBackendStatus('error');
    }
  };

  const handleLogin = async (username, password) => {
    try {
      const response = await apiService.login(username, password);
      if (response.success) {
        const sessionData = { username: response.username, expiry: Date.now() + 86400000 };
        localStorage.setItem('kafkaSession', JSON.stringify(sessionData));
        setIsAuthenticated(true);
        setCurrentUser(response.username);
        return true;
      }
      return false;
    } catch (err) {
      // Don't bypass authentication - show error instead
      console.error('Login failed:', err.response?.data?.message || err.message);
      return false;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('kafkaSession');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setSelectedCluster(null);
  };

  const handleJobCreated = (jobId) => {
    setCurrentJobId(jobId);
    setActiveTab('console');
  };

  const handleJobSelect = (jobId) => {
    setCurrentJobId(jobId);
    if (jobId) setActiveTab('console');
  };

  const handleSaveConfig = (config) => {
    localStorage.setItem('kafkaConfig', JSON.stringify(config));
    setSavedConfig(config);
  };

  const handleSelectCluster = (cluster) => {
    const savedCreds = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (savedCreds) setClusterCredentials(JSON.parse(savedCreds));
    setSelectedCluster(cluster);
  };

  const handleBackFromCluster = () => {
    setSelectedCluster(null);
    setClusterCredentials(null);
  };

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="h-4 w-4" />;
    if (theme === 'dark') return <Moon className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  if (!isAuthenticated) return <LoginForm onLogin={handleLogin} />;

  if (selectedCluster) {
    return (
      <TooltipProvider>
        <div className="min-h-screen bg-background flex flex-col">
          <Header theme={theme} setTheme={setTheme} backendStatus={backendStatus} currentUser={currentUser} handleLogout={handleLogout} setShowChangePassword={setShowChangePassword} getThemeIcon={getThemeIcon} subtitle="Cluster Dashboard" />
          <main className="container py-4 flex-1">
            <ClusterDashboard cluster={selectedCluster} credentials={clusterCredentials} onBack={handleBackFromCluster} />
          </main>
          <Footer />
          <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} username={currentUser} />
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex flex-col">
        <Header theme={theme} setTheme={setTheme} backendStatus={backendStatus} currentUser={currentUser} handleLogout={handleLogout} setShowChangePassword={setShowChangePassword} getThemeIcon={getThemeIcon} savedConfig={savedConfig} />

        <main className="container py-4 flex-1">
          {/* Navigation - Clusters button FIRST */}
          <div className="flex gap-2 mb-4">
            <Button variant={activeSection === 'management' ? 'default' : 'outline'} onClick={() => setActiveSection('management')} className="h-10">
              <Database className="h-4 w-4 mr-2" />Clusters
            </Button>
            <Button variant={activeSection === 'aws' ? 'default' : 'outline'} onClick={() => setActiveSection('aws')} className="h-10">
              <Cloud className="h-4 w-4 mr-2" />AWS
            </Button>
            <Button variant={activeSection === 'installation' ? 'default' : 'outline'} onClick={() => setActiveSection('installation')} className="h-10">
              <Rocket className="h-4 w-4 mr-2" />Install
            </Button>
            <Button variant={activeSection === 'uninstall' ? 'default' : 'outline'} onClick={() => setActiveSection('uninstall')} className="h-10">
              <Trash2 className="h-4 w-4 mr-2" />Uninstall
            </Button>
          </div>

          {activeSection === 'management' && (
            <Card className="border-border/50 p-4">
              <ClusterManager onSelectCluster={handleSelectCluster} />
            </Card>
          )}

          {activeSection === 'aws' && (
            <Card className="border-border/50 p-4">
              <AWSInfrastructure 
                onUseInstances={(instances) => {
                  // Pre-fill installation form with AWS instances
                  const config = {
                    nodes: instances.map((inst, i) => ({
                      host: inst.publicIp,
                      privateIp: inst.privateIp,
                      brokerId: i
                    }))
                  };
                  handleSaveConfig({ ...savedConfig, ...config });
                  setActiveSection('installation');
                }}
              />
            </Card>
          )}

          {activeSection === 'installation' && (
            <div className="flex gap-4">
              <aside className="w-72 flex-shrink-0">
                <JobStatusPanel currentJobId={currentJobId} onJobSelect={handleJobSelect} />
              </aside>
              <div className="flex-1 min-w-0">
                {backendStatus === 'error' && (
                  <Alert variant="destructive" className="mb-4 py-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle className="text-sm">Backend Connection Error</AlertTitle>
                    <AlertDescription className="text-xs">Unable to connect to the backend server.</AlertDescription>
                  </Alert>
                )}
                <Card className="border-border/50">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="border-b border-border/50 px-4 py-3">
                      <TabsList className="h-9 bg-muted/50">
                        <TabsTrigger value="install" className="text-xs h-8 px-4"><Rocket className="h-4 w-4 mr-2" />Install</TabsTrigger>
                        <TabsTrigger value="console" className="text-xs h-8 px-4" disabled={!currentJobId}><Terminal className="h-4 w-4 mr-2" />Console</TabsTrigger>
                      </TabsList>
                    </div>
                    <CardContent className="p-4">
                      <TabsContent value="install" className="mt-0">
                        <InstallationForm onJobCreated={handleJobCreated} savedConfig={savedConfig} onSaveConfig={handleSaveConfig} />
                      </TabsContent>
                      <TabsContent value="console" className="mt-0">
                        {currentJobId ? (
                          <ConsoleViewer jobId={currentJobId} savedConfig={savedConfig} onRetry={() => setActiveTab('install')} />
                        ) : (
                          <div className="flex flex-col items-center justify-center py-16 text-center">
                            <Terminal className="h-16 w-16 text-muted-foreground/30 mb-4" />
                            <h3 className="text-sm font-semibold mb-1">No Active Job</h3>
                            <p className="text-xs text-muted-foreground">Select a job from the sidebar or create a new installation</p>
                          </div>
                        )}
                      </TabsContent>
                    </CardContent>
                  </Tabs>
                </Card>
              </div>
            </div>
          )}

          {activeSection === 'uninstall' && (
            <div className="flex gap-4">
              <aside className="w-72 flex-shrink-0">
                <JobStatusPanel currentJobId={currentJobId} onJobSelect={handleJobSelect} />
              </aside>
              <div className="flex-1 min-w-0">
                <Card className="border-border/50">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="border-b border-border/50 px-4 py-3">
                      <TabsList className="h-9 bg-muted/50">
                        <TabsTrigger value="install" className="text-xs h-8 px-4"><Trash2 className="h-4 w-4 mr-2" />Uninstall</TabsTrigger>
                        <TabsTrigger value="console" className="text-xs h-8 px-4" disabled={!currentJobId}><Terminal className="h-4 w-4 mr-2" />Console</TabsTrigger>
                      </TabsList>
                    </div>
                    <CardContent className="p-4">
                      <TabsContent value="install" className="mt-0">
                        <UninstallForm onJobCreated={handleJobCreated} savedConfig={savedConfig} />
                      </TabsContent>
                      <TabsContent value="console" className="mt-0">
                        {currentJobId ? (
                          <ConsoleViewer jobId={currentJobId} savedConfig={savedConfig} onRetry={() => setActiveTab('install')} />
                        ) : (
                          <div className="flex flex-col items-center justify-center py-16 text-center">
                            <Terminal className="h-16 w-16 text-muted-foreground/30 mb-4" />
                            <h3 className="text-sm font-semibold mb-1">No Active Job</h3>
                            <p className="text-xs text-muted-foreground">Select a job from the sidebar</p>
                          </div>
                        )}
                      </TabsContent>
                    </CardContent>
                  </Tabs>
                </Card>
              </div>
            </div>
          )}
        </main>

        <Footer />
        <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} username={currentUser} />
      </div>
    </TooltipProvider>
  );
}

function Header({ theme, setTheme, backendStatus, currentUser, handleLogout, setShowChangePassword, getThemeIcon, savedConfig, subtitle }) {
  return (
    <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur">
      <div className="container flex h-14 items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-base font-bold leading-none">Kafka Cluster Manager</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{subtitle || 'Deploy & manage your infrastructure'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {savedConfig && <Badge variant="secondary" className="text-xs h-7 gap-1"><Save className="h-3 w-3" />Config Saved</Badge>}
          <Select value={theme} onValueChange={setTheme}>
            <SelectTrigger className="w-[110px] h-8 text-xs">
              <SelectValue><div className="flex items-center gap-2">{getThemeIcon()}{theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'}</div></SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light" className="text-xs"><div className="flex items-center gap-2"><Sun className="h-4 w-4" />Light</div></SelectItem>
              <SelectItem value="dark" className="text-xs"><div className="flex items-center gap-2"><Moon className="h-4 w-4" />Dark</div></SelectItem>
              <SelectItem value="system" className="text-xs"><div className="flex items-center gap-2"><Monitor className="h-4 w-4" />System</div></SelectItem>
            </SelectContent>
          </Select>
          <Badge variant={backendStatus === 'healthy' ? 'success' : backendStatus === 'checking' ? 'secondary' : 'destructive'} className="text-xs h-7">
            <span className={cn("mr-1.5 h-2 w-2 rounded-full", backendStatus === 'healthy' && "bg-green-300 animate-pulse", backendStatus === 'checking' && "bg-gray-400 animate-pulse", backendStatus === 'error' && "bg-red-300 animate-pulse")} />
            {backendStatus === 'healthy' ? 'Connected' : backendStatus === 'checking' ? 'Connecting...' : 'Disconnected'}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-2"><User className="h-4 w-4" />{currentUser}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs">My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowChangePassword(true)} className="text-xs cursor-pointer"><Key className="h-4 w-4 mr-2" />Change Password</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-xs cursor-pointer text-destructive"><LogOut className="h-4 w-4 mr-2" />Sign Out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border/50 bg-card/50 py-3 mt-auto">
      <div className="container text-center">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} <a href="https://www.f1arl.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">F1Soft Advanced Research Lab</a> - All Rights Reserved | Contact: <a href="mailto:f1arl@f1soft.com" className="text-primary hover:underline">F1 ARL &lt;f1arl@f1soft.com&gt;</a>
        </p>
      </div>
    </footer>
  );
}

export default App;
